// Inline SVG assets for the wrapped recap poster.
//
// Why inline strings (not React components)? The recap poster renders to an
// offscreen canvas via Image + svg-as-blob — we never mount these into the
// DOM. Strings keep the call site trivial: `Image.src = svgToDataUrl(SVG)`.
//
// All assets are sized + coloured so they composite cleanly onto the 1080×1920
// poster from recapCollage.ts. Colours match the canon palette tokens from
// apps/web/src/styles/canon.css (poster palette is the source of truth here —
// the wrapped poster is a fixed brand artifact, not palette-switched).

export type StampVariant = 'stumbledHere' | 'wrapped' | 'venue' | 'cheers'

const INK = '#17131d'
const PAPER = '#fffaf0'
const PINK = '#e95aa9'
const LIME = '#8bd93d'
const ORANGE = '#f06b28'
const TEAL = '#33b7c7'
const ACID = '#c8ff3f'

/** Stumble wordmark — bubble lowercase "stumble" with a pink dot over the i. */
export function wordmarkSvg(opts: { width?: number; fill?: string } = {}): string {
  const w = opts.width ?? 520
  const fill = opts.fill ?? INK
  // Render via the Bagel Fat One font; pink dot dotted onto the "t".
  // Font availability on the canvas is via CSS @font-face that the recap
  // generator awaits before drawing (see recapCollage.ts → loadDisplayFont).
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} 160" width="${w}" height="160">
  <text
    x="0"
    y="128"
    font-family="'Bagel Fat One','Atkinson Hyperlegible',system-ui,sans-serif"
    font-size="148"
    font-weight="400"
    fill="${fill}"
    letter-spacing="-2">stumble</text>
  <circle cx="${Math.round(w * 0.295)}" cy="40" r="14" fill="${PINK}" />
</svg>`
}

/** Round / rectangular stamps. */
export function stampSvg(variant: StampVariant, opts: { rotate?: number } = {}): string {
  const rotate = opts.rotate ?? 0
  switch (variant) {
    case 'stumbledHere':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320" width="320" height="320">
  <g transform="rotate(${rotate} 160 160)">
    <circle cx="160" cy="160" r="140" fill="none" stroke="${INK}" stroke-width="6" />
    <circle cx="160" cy="160" r="120" fill="none" stroke="${INK}" stroke-width="3" />
    <path id="topArc" d="M 40,160 A 120,120 0 0,1 280,160" fill="none" />
    <path id="botArc" d="M 280,160 A 120,120 0 0,1 40,160" fill="none" />
    <text font-family="'Special Elite','Courier New',monospace" font-size="36" fill="${INK}" letter-spacing="6">
      <textPath href="#topArc" startOffset="50%" text-anchor="middle">STUMBLED</textPath>
    </text>
    <text font-family="'Special Elite','Courier New',monospace" font-size="32" fill="${INK}" letter-spacing="8">
      <textPath href="#botArc" startOffset="50%" text-anchor="middle">★ HERE ★</textPath>
    </text>
    <circle cx="160" cy="160" r="44" fill="${PINK}" />
    <text x="160" y="174" font-family="'Bagel Fat One',sans-serif" font-size="42" fill="${PAPER}" text-anchor="middle">S</text>
  </g>
</svg>`

    case 'wrapped':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 140" width="360" height="140">
  <g transform="rotate(${rotate} 180 70)">
    <rect x="6" y="6" width="348" height="128" rx="10" fill="${PAPER}" stroke="${INK}" stroke-width="6" />
    <rect x="14" y="14" width="332" height="112" rx="6" fill="none" stroke="${INK}" stroke-width="2" stroke-dasharray="6 6" />
    <text x="180" y="64" font-family="'Special Elite','Courier New',monospace" font-size="26" fill="${INK}" text-anchor="middle" letter-spacing="4">STUMBLE</text>
    <text x="180" y="104" font-family="'Bagel Fat One',sans-serif" font-size="44" fill="${INK}" text-anchor="middle" letter-spacing="-1">WRAPPED</text>
  </g>
</svg>`

    case 'venue':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">
  <g transform="rotate(${rotate} 100 100)">
    <circle cx="100" cy="100" r="90" fill="${LIME}" stroke="${INK}" stroke-width="5" />
    <circle cx="100" cy="100" r="72" fill="none" stroke="${INK}" stroke-width="2" stroke-dasharray="4 4" />
    <text x="100" y="92" font-family="'Special Elite','Courier New',monospace" font-size="18" fill="${INK}" text-anchor="middle" letter-spacing="3">VISITED</text>
    <text x="100" y="124" font-family="'Bagel Fat One',sans-serif" font-size="28" fill="${INK}" text-anchor="middle">venue</text>
  </g>
</svg>`

    case 'cheers':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 280 120" width="280" height="120">
  <g transform="rotate(${rotate} 140 60)">
    <rect x="6" y="6" width="268" height="108" rx="14" fill="${ACID}" stroke="${INK}" stroke-width="5" />
    <text x="140" y="78" font-family="'Bagel Fat One',sans-serif" font-size="56" fill="${INK}" text-anchor="middle" letter-spacing="-1">cheers!</text>
  </g>
</svg>`
  }
}

/** Drink type icons — line-stroke variant of the DrinkTile glasses. */
export type DrinkKind = 'beer' | 'wine' | 'cocktail' | 'shot'

export function drinkIconSvg(kind: DrinkKind, opts: { fill?: string } = {}): string {
  const fill = opts.fill ?? INK
  switch (kind) {
    case 'beer':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 140" width="100" height="140">
  <g fill="${ORANGE}" stroke="${fill}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M22 30 L22 130 Q22 134 27 134 L73 134 Q78 134 78 130 L78 30 Z" />
    <path d="M78 50 Q92 50 92 70 L92 92 Q92 108 78 108" fill="none" />
    <path d="M22 30 Q50 25 78 30" fill="none" />
  </g>
</svg>`
    case 'wine':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 180" width="100" height="180">
  <g fill="${PINK}" stroke="${fill}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M25 30 Q25 80 50 82 Q75 80 75 30 Q50 26 25 30 Z" />
    <line x1="50" y1="82" x2="50" y2="148" />
    <line x1="28" y1="150" x2="72" y2="150" />
  </g>
</svg>`
    case 'cocktail':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 180" width="100" height="180">
  <g fill="${TEAL}" stroke="${fill}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M15 28 L50 86 L85 28 Q50 24 15 28 Z" />
    <line x1="50" y1="86" x2="50" y2="148" />
    <line x1="28" y1="150" x2="72" y2="150" />
  </g>
  <circle cx="58" cy="58" r="6" fill="${LIME}" stroke="${fill}" stroke-width="3" />
  <line x1="58" y1="58" x2="78" y2="30" stroke="${fill}" stroke-width="3" />
</svg>`
    case 'shot':
      return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 90" width="100" height="90">
  <g fill="${ACID}" stroke="${fill}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M22 22 L18 70 Q18 74 22 74 L78 74 Q82 74 78 22 Q50 17 22 22 Z" />
    <path d="M22 22 Q50 17 78 22" fill="none" />
    <line x1="14" y1="80" x2="86" y2="80" stroke-width="7" />
  </g>
</svg>`
  }
}

/** Turn an SVG string into an HTMLImageElement (resolved after .complete). */
export function svgToImage(svg: string): Promise<HTMLImageElement> {
  // data: URL keeps this fully offline — no fetch, no CORS, no service worker.
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(new Error(`SVG asset failed to load: ${String(e)}`))
    img.src = url
  })
}

export const RECAP_PALETTE = {
  INK,
  PAPER,
  PINK,
  LIME,
  ORANGE,
  TEAL,
  ACID,
} as const
